<template>
  <header class="topbar">
    <div class="left">
      <div v-if="user?.ready" class="user" title="LINE 使用者">
        <img v-if="user.pictureUrl" :src="user.pictureUrl" class="avatar" />
        <div class="name">{{ user.displayName || user.userId }}</div>
      </div>

      <div class="brand">
        <div class="dot"></div>
        <div class="title">
          <div class="h1">AICard Notebook</div>
        </div>
      </div>
    </div>

    <div class="right">
      <!-- ✅ 手機/桌面都保留上傳入口 -->
      <button class="btn ghost btn-pick" type="button" @click="$emit('pick')">
        <span class="icon" aria-hidden="true">⬆️</span>
        <span class="label">上傳新檔案</span>
      </button>

      <button
        class="btn primary btn-download"
        type="button"
        :disabled="!pdfLoaded || isExporting"
        @click="$emit('download')"
      >
        <span class="icon" aria-hidden="true">⬇️</span>
        <span class="label">{{ isExporting ? '輸出中…' : '下載 PDF' }}</span>
      </button>
    </div>
  </header>
</template>

<script setup lang="ts">
defineProps<{
  pdfLoaded: boolean
  isExporting: boolean
  user?: { ready: boolean; pictureUrl?: string; displayName?: string; userId?: string }
}>()

defineEmits<{
  (e: 'pick'): void
  (e: 'download'): void
}>()
</script>

<style scoped lang="scss">
.topbar {
  height: 64px;
  flex-shrink: 0;
  padding: 0 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  border-bottom: 1px solid rgba(255,255,255,0.06);
  background: linear-gradient(180deg, rgba(10,18,33,0.95), rgba(5,10,18,0.92));
}

.topbar .left {
  display: flex;
  align-items: center; /* ✅ 讓 user pill 與標題同水平線 */
  gap: 12px;
  min-width: 0;
  flex: 1 1 auto;
}

.topbar .right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
  min-width: 0;
}

/* user pill */
.topbar .user {
  display: inline-flex;
  align-items: center; /* ✅ 確保頭像與文字垂直置中 */
  gap: 10px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid rgba(255,255,255,0.10);
  background: rgba(10,18,33,0.55);
  min-width: 0;
  max-width: 260px; /* 桌面稍微放寬 */
}

.topbar .user .avatar {
  width: 26px;  /* ✅ 稍微小一點，避免擠壓 */
  height: 26px;
  border-radius: 999px;
  object-fit: cover;
  flex: 0 0 26px;
  display: block;
}

.topbar .user .name {
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 700;
  font-size: 13px;
  line-height: 1;
}

/* brand */
.topbar .brand {
  display: inline-flex;
  align-items: center; /* ✅ dot 與文字對齊 */
  gap: 10px;
  min-width: 0;
}

.topbar .brand .dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: #1d7bff;
  box-shadow: 0 0 18px rgba(29,123,255,0.55);
  flex: 0 0 10px;
}

.topbar .brand .title {
  min-width: 0;
}

.topbar .brand .title .h1 {
  font-weight: 800;
  font-size: 14px;
  line-height: 1;
  white-space: nowrap;
}

/* buttons */
.btn {
  height: 40px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid rgba(255,255,255,0.10);
  background: rgba(255,255,255,0.05);
  color: #e8eefc;
  cursor: pointer;
  font-weight: 700;
  transition: 0.15s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.btn:hover {
  background: rgba(255,255,255,0.08);
  border-color: rgba(255,255,255,0.14);
}

.btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.btn.ghost {
  background: rgba(255,255,255,0.03);
}

.btn.primary {
  background: linear-gradient(180deg, rgba(29,123,255,1), rgba(29,123,255,0.85));
  border-color: rgba(29,123,255,0.55);
  box-shadow: 0 10px 22px rgba(29,123,255,0.20);
}

.btn.primary:hover {
  box-shadow: 0 12px 28px rgba(29,123,255,0.26);
}

/* default: icon hidden (desktop shows text) */
.btn .icon {
  display: none;
  font-size: 16px;
  line-height: 1;
}

/* ✅ 手機版：縮高、藏文字、保留 icon（避免擠爆 + 保證上傳入口存在） */
@media (max-width: 720px) {
  .topbar {
    height: 56px;
    padding: 0 10px;
    gap: 10px;
  }

  .topbar .user {
    max-width: 120px;
    padding: 5px 8px;
    gap: 8px;
  }

  .topbar .user .avatar {
    width: 24px;
    height: 24px;
    flex-basis: 24px;
  }

  .topbar .brand .title .h1 {
    font-size: 13px;
  }

  .btn {
    height: 36px;
    padding: 0 12px;
    gap: 6px;
  }

  .btn .icon {
    display: inline-block;
  }

  .btn .label {
    display: none; /* ✅ 只留 icon，不會擠壓左側 */
  }
}

/* ✅ 更窄（例如 360px）：進一步縮 user，避免壓到 brand */
@media (max-width: 390px) {
  .topbar .user {
    max-width: 96px;
  }
}
</style>
